/*
 * <collection-leg-filter>: the floating "Filter by wide leg" row on v3 collection
 * pages (client, 2026-09-24). Markup, settings and CSS are in
 * snippets/collection-leg-filter.liquid.
 *
 * The row shows while BOTH hold:
 *   - the active category pill is one the row is enabled for (the section marks
 *     those pills data-leg-filter; JEANS and CORDUROY by default), and
 *   - the category + sub-filter rows have scrolled up behind the header and the
 *     row, i.e. the row would be covering them anyway. One IntersectionObserver
 *     on those rows decides it; nothing runs per scroll frame.
 *
 * Where it lives. The bar is rendered by the grid section but moved into the
 * header-v3 section when this element connects, so it rides the header's own
 * sticky layer. A second pinned layer of its own (fixed, or sticky inside <main>)
 * would be positioned by iOS separately from the header and can drift against it
 * by a few px during toolbar morphs and bounces, which reads as the gap between
 * the two cards flickering; inside the header's layer they can only move
 * together. It also inherits the header's hide-on-scroll transform for free: in
 * the header's "reveal" mode the row slides up into the header's place.
 *
 * What a band applies. Every Leg Opening value whose label's number falls in the
 * band's range (values are matched by label because Search & Discovery groups
 * submit a gid, not a size). The value list is the server's, plus anything the
 * facets show later. The request goes through FacetFiltersForm.renderPage, the
 * same AJAX swap and grid cache the category pills use, and every other param in
 * the URL is kept, so a band applies within the active category and sub-filter.
 * A category pill click drops the leg param again (collection-quick-filters.js,
 * data-reset-on-parent-change), just as it resets the sub-filter.
 *
 * Bands with no products in the current view are hidden, from the same facet
 * counts the filter drawer shows. Counts for values of one filter do not narrow
 * each other (a 19-23" selection leaves 24" and 26" counted), so the other bands
 * stay correct while one is applied.
 *
 * The FILTER badge. While a band is the applied filter, the bar carries
 * data-nav-param and filter-drawer.js leaves that param out of the count, the
 * same rule as the category and sub-filter rows: navigation is not counted. A
 * leg-opening choice made in the drawer that matches no band is not claimed, so
 * it still counts.
 *
 * The guide. The info button at the right end of the bar opens the leg-opening
 * guide, a native modal <dialog> that the snippet renders next to this element.
 * It does not move with the bar; instead it is pinned to the bar's bottom edge,
 * at the bar's width, from a measurement taken when it opens (and on resize), so
 * it drops out of the bar like a panel. The browser handles Escape, keeps Tab
 * inside and makes the page inert; this adds Close, closing on a backdrop click,
 * focus, and warming the example images before the first open.
 */
if (!customElements.get('collection-leg-filter')) {
  // First number in a label or bound: 26" -> 26, 32+ -> 32. Any inch mark works,
  // curly or straight (one live value is typed with a curly one).
  const readNumber = (text) => {
    const match = String(text || '').match(/\d+(?:\.\d+)?/);
    return match ? parseFloat(match[0]) : NaN;
  };

  class CollectionLegFilter extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      // Kept across a disconnect: after the first mount the bar is no longer a
      // child of this element.
      this.bar = this.bar || this.querySelector('[data-leg-filter-bar]');
      if (!this.bar) return;
      this.initialized = true;

      this.param = this.dataset.param;
      this.quickFilters = document.querySelector(
        `collection-quick-filters[data-section-id="${this.dataset.sectionId}"]`
      );
      this.subnav = this.quickFilters ? this.quickFilters.querySelector('.collection-quick-filters__scroller') : null;
      this.pills = this.quickFilters ? Array.from(this.quickFilters.querySelectorAll('[data-quick-filter-btn]')) : [];

      this.allButton = this.bar.querySelector('[data-leg-band="all"]');
      this.bands = Array.from(this.bar.querySelectorAll('[data-leg-band="range"]')).map((button) => {
        const min = readNumber(button.dataset.min);
        const max = button.dataset.max === '' ? Infinity : readNumber(button.dataset.max);
        return { button, min, max, invalid: Number.isNaN(min) || Number.isNaN(max) || max < min };
      });

      // value -> the number in its label. Insertion order is the server's, which
      // keeps the URL a band builds stable, and with it the grid cache key.
      if (!this.known) {
        this.known = new Map();
        const template = this.querySelector('template[data-leg-filter-values]');
        if (template) {
          template.content.querySelectorAll('data').forEach((el) => this.learn(el.value, el.textContent));
        }
      }
      this.counts = null;

      this.onClick = this.onBandClick.bind(this);
      [this.allButton, ...this.bands.map((band) => band.button)].forEach((button) => {
        if (button) button.addEventListener('click', this.onClick);
      });

      this.card = this.bar.querySelector('.collection-leg-filter__card');
      this.infoButton = this.bar.querySelector('[data-leg-filter-info]');
      this.guide = this.infoButton ? document.getElementById(this.infoButton.getAttribute('aria-controls')) : null;
      if (this.guide && typeof this.guide.showModal === 'function') {
        this.bindGuide();
      } else if (this.infoButton) {
        this.infoButton.hidden = true;
      }

      // A category pill changing (click, back/forward, or quick-filters' own
      // sync) decides whether the row applies at all.
      this.pillObserver = new MutationObserver(() => this.sync());
      this.pills.forEach((pill) => this.pillObserver.observe(pill, { attributes: true, attributeFilter: ['class'] }));

      // AJAX renders hand over the fetched section, whose facets carry the new
      // counts, before the grid swaps.
      this.onFacets = (event) => this.sync(event.detail && event.detail.doc);
      document.addEventListener('facets:filters-updated', this.onFacets);

      this.onPopState = () => this.sync();
      window.addEventListener('popstate', this.onPopState);

      // Settles after ANY grid change, the filter drawer's included (it swaps the
      // grid itself and fires no event). By then the drawer's checkboxes are
      // current, which is when the FILTER badge can be recounted safely.
      const grid = document.getElementById('ProductGridContainer');
      if (grid) {
        this.gridObserver = new MutationObserver(() => {
          clearTimeout(this.gridTimer);
          this.gridTimer = setTimeout(() => this.settle(), 150);
        });
        this.gridObserver.observe(grid, { childList: true, subtree: true });
      }

      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (this.mounted && this.coverLine() !== this.line) this.observeSubnav();
        }, 150);
      };
      window.addEventListener('resize', this.onResize, { passive: true });

      // Theme editor: re-rendering the header replaces the node the bar lives in.
      if (window.Shopify && window.Shopify.designMode) {
        this.onSectionLoad = () => {
          if (this.bar && !this.bar.isConnected) this.mount();
        };
        document.addEventListener('shopify:section:load', this.onSectionLoad);
      }

      this.mount();
      this.settle();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.initialized = false;
      if (this.io) this.io.disconnect();
      if (this.pillObserver) this.pillObserver.disconnect();
      if (this.gridObserver) this.gridObserver.disconnect();
      clearTimeout(this.gridTimer);
      clearTimeout(this.resizeTimer);
      document.removeEventListener('facets:filters-updated', this.onFacets);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('resize', this.onResize);
      if (this.onSectionLoad) document.removeEventListener('shopify:section:load', this.onSectionLoad);
      [this.allButton, ...this.bands.map((band) => band.button)].forEach((button) => {
        if (button) button.removeEventListener('click', this.onClick);
      });
      this.unbindGuide();
      // The bar sits in the header, outside this element: take it along.
      this.bar.remove();
      this.mounted = false;
      this.shown = undefined;
    }

    mount() {
      // A theme-editor re-render of the grid section leaves the previous bar in
      // the header. querySelectorAll, not getElementById: the fresh copy is
      // earlier in tree order and getElementById would return it, not the stale
      // one (the reviews-modal lesson in CLAUDE.md).
      document.querySelectorAll(`[id="${this.bar.id}"]`).forEach((el) => {
        if (el !== this.bar) el.remove();
      });

      const header = document.querySelector('header.header-v3');
      const headerSection = header ? header.closest('.shopify-section') : null;
      const pinned = !!headerSection && /^(sticky|fixed)$/.test(getComputedStyle(headerSection).position);
      if (pinned) {
        header.appendChild(this.bar);
      } else {
        document.body.appendChild(this.bar);
      }
      this.headerSection = pinned ? headerSection : null;
      this.bar.classList.toggle('collection-leg-filter__bar--fixed', !pinned);
      this.bar.hidden = false;
      this.mounted = true;
      this.observeSubnav();
    }

    // The bottom edge of the row while it shows under a visible header, in
    // viewport px: the header section (its card plus top gap) plus the bar (the
    // row's card plus its own top gap). The layout box is read, so the bar's
    // hide transform does not matter.
    coverLine() {
      return Math.round((this.headerSection ? this.headerSection.offsetHeight : 0) + this.bar.offsetHeight);
    }

    observeSubnav() {
      if (this.io) this.io.disconnect();
      this.line = this.coverLine();
      if (!this.subnav) return;
      // The root is the viewport below the row's bottom edge, so "not
      // intersecting, and above it" means the category rows are fully covered by
      // the header and the row: the row appears exactly as they disappear under
      // it, and disappears as they come back out.
      this.io = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          const top = entry.rootBounds ? entry.rootBounds.top : this.line;
          this.pastSubnav = !entry.isIntersecting && entry.boundingClientRect.bottom <= top + 1;
          this.render();
        },
        { rootMargin: `-${this.line}px 0px 0px 0px` }
      );
      this.io.observe(this.subnav);
    }

    learn(value, labelText) {
      if (!value || this.known.has(value)) return;
      const number = readNumber(labelText);
      if (!Number.isNaN(number)) this.known.set(value, number);
    }

    // value -> product count in the current view (NaN when unreadable), from
    // whichever of the filter drawer's or Dawn's facet markup `root` carries.
    // null when it carries none for this filter.
    readCounts(root) {
      if (!root || !this.param) return null;
      const counts = new Map();
      root.querySelectorAll(`input[name="${CSS.escape(this.param)}"]`).forEach((input) => {
        if (counts.has(input.value)) return;
        const label = input.closest('label');
        if (!label) return;
        this.learn(input.value, label.textContent);
        let count = parseInt(label.dataset.count, 10);
        if (Number.isNaN(count)) {
          const countEl = label.querySelector('.filter-drawer__checkbox-count');
          const match = (countEl || label).textContent.match(/\((\d+)\)/);
          count = match ? parseInt(match[1], 10) : NaN;
        }
        counts.set(input.value, count);
      });
      return counts.size ? counts : null;
    }

    inBand(band, number) {
      // The upper bound takes its decimals: 24-26 includes 26.5.
      return number >= band.min && number < band.max + 1;
    }

    valuesFor(band) {
      const values = [];
      this.known.forEach((number, value) => {
        if (this.inBand(band, number)) values.push(value);
      });
      return values;
    }

    // Unknown counts (no facet markup at all) never hide anything.
    valueHasProducts(value) {
      if (!this.counts) return true;
      const count = this.counts.get(value);
      if (count === undefined) return false;
      return Number.isNaN(count) || count > 0;
    }

    // 'all' when the URL applies no leg opening; otherwise the band that the
    // applied values fill: every value in its range, and every value of it that
    // has products applied. A drawer choice that is only part of a band (26"
    // alone) matches none, so no button claims it.
    activeFor(urlValues) {
      if (!urlValues.length) return 'all';
      return (
        this.bands.find((band) => {
          if (band.invalid) return false;
          const inside = urlValues.every((value) => this.known.has(value) && this.inBand(band, this.known.get(value)));
          if (!inside) return false;
          return this.valuesFor(band).every((value) => urlValues.includes(value) || !this.valueHasProducts(value));
        }) || null
      );
    }

    parentEligible() {
      const pill = this.pills.find((button) => button.classList.contains('is-active'));
      return !!pill && pill.hasAttribute('data-leg-filter');
    }

    sync(doc = null) {
      if (!this.initialized) return;
      const counts =
        (doc && this.readCounts(doc)) ||
        this.readCounts(document.querySelector('filter-drawer')) ||
        this.readCounts(document.getElementById('main-collection-filters'));
      if (counts) this.counts = counts;

      const urlValues = new URLSearchParams(window.location.search).getAll(this.param);
      const active = this.activeFor(urlValues);

      let anyBand = false;
      this.bands.forEach((band) => {
        const hasProducts = !band.invalid && this.valuesFor(band).some((value) => this.valueHasProducts(value));
        if (hasProducts) anyBand = true;
        band.button.hidden = band.invalid || !(hasProducts || band === active);
      });

      this.setPressed(active);
      this.eligible = this.parentEligible() && (anyBand || (!!active && active !== 'all'));
      this.claim(this.eligible && !!active && active !== 'all');
      this.render();
    }

    // sync(), then recount the FILTER badge if the claim changed. Only here, once
    // the grid has settled: at click time the drawer's checkboxes still show the
    // old selection, and recounting against them flashes a wrong number.
    settle() {
      this.sync();
      const claimed = this.bar.hasAttribute('data-nav-param');
      if (claimed === this.settledClaim) return;
      this.settledClaim = claimed;
      const drawer = document.querySelector('filter-drawer');
      if (drawer && typeof drawer.updateActiveFilterCount === 'function') drawer.updateActiveFilterCount();
    }

    claim(on) {
      if (on) {
        this.bar.setAttribute('data-nav-param', this.param);
      } else {
        this.bar.removeAttribute('data-nav-param');
      }
    }

    setPressed(active) {
      const pairs = [[this.allButton, active === 'all'], ...this.bands.map((band) => [band.button, band === active])];
      pairs.forEach(([button, on]) => {
        if (!button) return;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    render() {
      const show = !!(this.mounted && this.eligible && this.pastSubnav);
      if (show === this.shown) return;
      this.shown = show;
      this.bar.classList.toggle('is-visible', show);
      // header-v3 counts the strip in the page's scroll padding while it shows.
      this.bar.dispatchEvent(new Event('collection-leg-filter:toggle', { bubbles: true }));
    }

    onBandClick(event) {
      const button = event.currentTarget;
      if (button.getAttribute('aria-pressed') === 'true') return;
      const band = button === this.allButton ? 'all' : this.bands.find((item) => item.button === button);
      if (!band) return;
      const values = band === 'all' ? [] : this.valuesFor(band);
      if (band !== 'all' && !values.length) return;

      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete(this.param);
      values.forEach((value) => params.append(this.param, value));

      if (typeof FacetFiltersForm === 'undefined' || !document.getElementById('product-grid')) {
        const query = params.toString();
        window.location.assign(`${window.location.pathname}${query ? `?${query}` : ''}`);
        return;
      }

      this.scrollToResults();
      FacetFiltersForm.renderPage(params.toString());
      document.dispatchEvent(new CustomEvent('collection:url-changed', { detail: { url: window.location.href } }));
      this.setPressed(band);
      this.claim(band !== 'all');
    }

    bindGuide() {
      this.onGuideOpen = () => this.openGuide();
      this.onGuideIntent = () => this.warmGuide();
      this.infoButton.addEventListener('click', this.onGuideOpen);
      this.infoButton.addEventListener('pointerenter', this.onGuideIntent, { passive: true });
      this.infoButton.addEventListener('focus', this.onGuideIntent);

      // A backdrop press is one that starts AND ends outside the dialog's box:
      // a drag that selects text and ends on the backdrop is not a close. The
      // target alone can't tell, because a press on the dialog's own scrollbar
      // also targets the dialog.
      this.onGuidePointerDown = (event) => {
        this.backdropPress = this.isBackdropEvent(event);
      };
      this.onGuideClick = (event) => {
        if (event.target.closest('[data-leg-filter-guide-close]')) {
          this.guide.close();
        } else if (this.backdropPress && this.isBackdropEvent(event)) {
          this.guide.close();
        }
        this.backdropPress = false;
      };
      this.guide.addEventListener('pointerdown', this.onGuidePointerDown);
      this.guide.addEventListener('click', this.onGuideClick);

      // The browser returns focus to whatever had it before the dialog opened.
      // After a tap in iOS Safari that is <body> (a tapped button never takes
      // focus there), so send it back to the info button instead. global.js
      // keeps a focus() that follows a tap from drawing a ring.
      this.onGuideClose = () => {
        this.bar.classList.remove('is-guide-open');
        window.removeEventListener('resize', this.onGuideResize);
        const active = document.activeElement;
        if (!this.infoButton.isConnected) return;
        if (!active || active === document.body || this.guide.contains(active)) {
          this.infoButton.focus({ preventScroll: true });
        }
      };
      this.guide.addEventListener('close', this.onGuideClose);
      this.onGuideResize = () => this.placeGuide();
    }

    unbindGuide() {
      if (!this.onGuideOpen) return;
      this.infoButton.removeEventListener('click', this.onGuideOpen);
      this.infoButton.removeEventListener('pointerenter', this.onGuideIntent);
      this.infoButton.removeEventListener('focus', this.onGuideIntent);
      this.guide.removeEventListener('pointerdown', this.onGuidePointerDown);
      this.guide.removeEventListener('click', this.onGuideClick);
      this.guide.removeEventListener('close', this.onGuideClose);
      window.removeEventListener('resize', this.onGuideResize);
      if (this.guide.open) this.guide.close();
      this.bar.classList.remove('is-guide-open');
      this.onGuideOpen = null;
    }

    isBackdropEvent(event) {
      if (event.target !== this.guide) return false;
      const rect = this.guide.getBoundingClientRect();
      return (
        event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom
      );
    }

    // The example images are lazy and the closed dialog is display:none, so
    // they would not start until the guide opens. Pointing at or focusing the
    // button switches them to eager, which downloads them while still hidden.
    warmGuide() {
      if (this.guideWarmed || !this.guide) return;
      this.guideWarmed = true;
      this.guide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    // Pins the guide under the strip: its top on the strip's bottom edge, its
    // sides on the strip's sides. Measured after showModal(), because locking
    // the page's scroll can remove a desktop scrollbar and widen the header.
    placeGuide() {
      if (!this.guide || !this.card) return;
      const rect = this.card.getBoundingClientRect();
      const radius = getComputedStyle(this.bar).getPropertyValue('--hv3-card-radius').trim();
      const style = this.guide.style;
      style.setProperty('--leg-guide-top', `${rect.bottom}px`);
      style.setProperty('--leg-guide-left', `${rect.left}px`);
      style.setProperty('--leg-guide-width', `${rect.width}px`);
      if (radius) style.setProperty('--leg-guide-radius', radius);
    }

    openGuide() {
      if (!this.guide || this.guide.open) return;
      this.warmGuide();
      // Keeps the strip out (even when only keyboard focus was showing it) and
      // drops its slide, so it is where it will stay when it is measured.
      this.bar.classList.add('is-guide-open');
      this.guide.showModal();
      this.placeGuide();
      window.addEventListener('resize', this.onGuideResize, { passive: true });
      // The dialog itself, not its close button: a screen reader starts at the
      // guide's title and text, the first Tab still reaches the close button,
      // and after a tap no ring is drawn on it (Safari rings the close button
      // that showModal focuses; global.js's focus() wrapper prevents that here).
      this.guide.focus();
    }

    // A shopper deep in the grid who picks a band would otherwise land wherever
    // the shorter result set clamps them, often the footer. Take them to the
    // first results instead: the category rows' bottom edge is parked just above
    // the row's, so the grid starts right under the row and the row stays up.
    // Never scrolls down, and only while the row is showing.
    scrollToResults() {
      if (!this.shown || !this.subnav) return;
      const delta = this.subnav.getBoundingClientRect().bottom - (this.line - 2);
      if (delta < -1) window.scrollTo({ top: window.scrollY + delta, behavior: 'auto' });
    }
  }

  customElements.define('collection-leg-filter', CollectionLegFilter);
}
